import {
  createApi,
  fetchBaseQuery,
  FetchArgs,
  BaseQueryApi,
} from "@reduxjs/toolkit/query/react";
import { setCredentials, logout } from "./auth/reducer";
import { API, API_SLICE_TAGS, LOCAL_STORAGE } from "../../globals/globals";
import { IRetrievedTokenData } from "./auth/types";
import { AppState } from "../";
import { isJwtExpired, isJwtExpiring } from "../../utils/helpers";

const baseQuery = fetchBaseQuery({
  baseUrl: API.BASE_URL,
  timeout: 100000,
  credentials: "include",

  prepareHeaders: async (headers, { getState }) => {
    const { idToken } = (getState() as AppState).authentication;
    const token = idToken ?? localStorage.getItem(LOCAL_STORAGE.TOKEN);

    if (token) {
      headers.set("authorization", `Bearer ${token}`);
    }

    return headers;
  },
});

let refreshInProgress: Promise<any> | null = null;

const baseQueryWithReauth = async (
  args: FetchArgs,
  api: BaseQueryApi,
  extraOptions: any
) => {
  let result = await baseQuery(args, api, extraOptions);

  if ((result.error && result.error.status === 401) || isJwtExpiring(localStorage.getItem(LOCAL_STORAGE.TOKEN))) {
    // 401 Unauthorized response, determine if it is due to an expired token
    const token = localStorage.getItem(LOCAL_STORAGE.TOKEN);

    // If the 401 is due to expired token or about to expire, attempt to refresh
    if (token && (isJwtExpiring(token) || isJwtExpired(token))) {
      if (!refreshInProgress) {
        console.log("Token is expired, attempting to refresh...");
        refreshInProgress = refreshAuthToken(api, extraOptions);
      }

      try {
        const newToken = await refreshInProgress;
        const newHeaders = new Headers();
        newHeaders.set("authorization", `Bearer ${newToken}`);
        result = await baseQuery({ ...args, headers: newHeaders }, api, extraOptions);
      } catch (error) {
        return Promise.reject(error);
      } finally {
        refreshInProgress = null;
      }
    }
  }

  return result;
};

const refreshAuthToken = async (api: BaseQueryApi, extraOptions: any) => {
  const refreshToken = localStorage.getItem(LOCAL_STORAGE.REFRESH_TOKEN);
  if (!refreshToken) {
    api.dispatch(logout());
    return Promise.reject('No refresh token available');
  }

  const refreshHeaders = new Headers();
  refreshHeaders.set("authorization", `Bearer ${refreshToken}`);
  const refreshResult = await fetchBaseQuery({
    baseUrl: API.BASE_URL,
    timeout: 10000,
    credentials: "include",
  })({ url: API.ENDPOINTS.REFRESH.path, headers: refreshHeaders }, api, extraOptions);

  if (refreshResult.data) {
    const data = refreshResult.data as IRetrievedTokenData;
    //console.log(`Received a new token: ${data.idToken.slice(0, 9)}...`);
    api.dispatch(setCredentials(data));
    return data.idToken;
  } else {
    api.dispatch(logout());
    return Promise.reject(refreshResult.error);
  }
};

export const apiSlice = createApi({
  baseQuery: baseQueryWithReauth,
  tagTypes: Object.values(API_SLICE_TAGS),
  keepUnusedDataFor: 60, // Keep cached data for 60 seconds after component unmounts

  endpoints: (builder) => ({}),
});
